/**
 * Achievements bridge — maps server-side unlocked achievement keys (from
 * `PATCH /chapters/:id`, `POST /bible/:projectId` and `GET /achievements`)
 * to the client's moment-celebration system (see `components/moments.tsx`).
 *
 * The server is the source of truth and is idempotent — the same key can
 * come back on retries/refetches without meaning "unlock again". We keep a
 * lightweight localStorage cache purely to avoid re-flickering the same
 * celebration twice in the same browser (anti-flicker, not a lock).
 */

type MomentDetail = Record<string, unknown> & { type: string };

const CACHE_PREFIX = 'ach-';

function alreadyShown(key: string): boolean {
  try {
    return localStorage.getItem(CACHE_PREFIX + key) === '1';
  } catch {
    return false;
  }
}

function markShown(key: string): void {
  try {
    localStorage.setItem(CACHE_PREFIX + key, '1');
  } catch {
    /* localStorage unavailable — ignore, worst case is a re-flicker */
  }
}

function dispatchMoment(detail: MomentDetail): void {
  window.dispatchEvent(new CustomEvent('reformu:moment', { detail }));
}

/** Pulls the trailing `-N` (integer) off a key, if present (e.g. `souls-<pid>-10` → 10). */
function trailingNumber(key: string): number | null {
  const m = key.match(/-(\d+)$/);
  return m ? Number(m[1]) : null;
}

/** Maps one server achievement key to a moments-system scene, or null if there's no match. */
function sceneFor(key: string): MomentDetail | null {
  if (key.startsWith('candle-')) return { type: 'candle' };
  if (key.startsWith('legendary-')) return { type: 'legendary' };
  if (key.startsWith('mythic-')) return { type: 'mythic' };
  if (key.startsWith('auroranight-')) return { type: 'auroranight' };

  if (key.startsWith('streak-')) {
    const n = trailingNumber(key);
    if (n === 7 || n === 30 || n === 100) return { type: 'streak', nights: n };
    return null;
  }

  if (key.startsWith('fullweek-')) return { type: 'fullweek' };
  if (key.startsWith('monthstars-')) return { type: 'monthstars', nights: 20 };

  if (key === 'hundredk') return { type: 'hundredk' };

  if (key.startsWith('record-')) {
    // sufixo é a DATA (não contagem); o watcher ao vivo já toca o recorde com
    // o número real — do servidor só persiste, sem cena
    return null;
  }

  if (key.startsWith('crossing-')) {
    const pct = trailingNumber(key);
    if (pct === null) return null;
    return { type: 'crossing', pct, title: '' };
  }

  if (key.startsWith('dawn-')) return { type: 'dawn', title: '' };
  if (key.startsWith('spine10-')) return { type: 'spine10', title: '' };
  if (key.startsWith('firstword-')) return { type: 'firstword', title: '' };

  if (key.startsWith('souls-')) {
    const n = trailingNumber(key);
    if (n === null) return null;
    if (n === 10) return { type: 'tensouls' };
    if (n === 1000) return { type: 'thousand' };
    return { type: 'archivemilestone', count: n };
  }

  return null;
}

/**
 * Consumes the `unlocked` keys returned by the server after a chapter update
 * or bible entry creation (or from `GET /achievements`), and plays the
 * matching celebration for each — skipping the ones already shown in this
 * browser (anti-flicker only; the underlying achievement is server-owned).
 */
export function applyServerUnlocks(unlocked: string[] | undefined): void {
  if (!unlocked || unlocked.length === 0) return;
  for (const key of unlocked) {
    if (alreadyShown(key)) continue;
    const detail = sceneFor(key);
    if (!detail) continue;
    markShown(key);
    dispatchMoment(detail);
  }
}
